import { cs } from '../../lib/currency';
/**
 * AdminAddUniformOrderDialog — admin creates an order on behalf of a
 * parent. iter307aj.
 *
 * Flow:
 *   1. Admin picks a parent (searchable select; uses /admin/parents).
 *   2. Picks "For parent themselves" OR a player belonging to that
 *      parent (auto-loaded via /admin/players?parent_id=...).
 *   3. Picks one or more items from the active campaign's `items`
 *      config + quantity per item.
 *   4. Optional notes.
 *   5. Submit → POST /uniform/orders/admin-create.
 *
 * Marking the order "Processed" later (separate action) auto-generates
 * an unpaid invoice under the "Uniform" category for the same parent.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Plus } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

// iter335 — resolve invoiced per-unit price: fixed `price` when set,
// else legacy `price_min`. Mirrors backend _item_unit_price.
const unitPrice = (cfg) => {
  const p = Number(cfg?.price || 0);
  return p > 0 ? p : Number(cfg?.price_min || 0);
};

export default function AdminAddUniformOrderDialog({
  open, onOpenChange, campaign, onCreated,
}) {
  const [parents, setParents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [parentQuery, setParentQuery] = useState('');
  const [parentId, setParentId] = useState('');
  const [playerId, setPlayerId] = useState('__parent_self__');
  const [items, setItems] = useState({});   // { item_id: { qty, selected } }
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset state whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setParentQuery('');
    setParentId('');
    setPlayerId('__parent_self__');
    setItems({});
    setNotes('');
  }, [open]);

  // Load parents once on open.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get('/parents');
        setParents(Array.isArray(res.data) ? res.data : []);
      } catch {
        setParents([]);
      }
    })();
  }, [open]);

  // Load players for the chosen parent.
  useEffect(() => {
    if (!parentId) { setPlayers([]); return; }
    (async () => {
      try {
        const res = await api.get(`/parents/${parentId}/children`);
        setPlayers(Array.isArray(res.data) ? res.data : []);
      } catch {
        setPlayers([]);
      }
    })();
  }, [parentId]);

  const filteredParents = useMemo(() => {
    const q = parentQuery.trim().toLowerCase();
    if (!q) return parents.slice(0, 50);
    return parents
      .filter((p) => (p.name || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q))
      .slice(0, 50);
  }, [parents, parentQuery]);

  const computedTotal = useMemo(() => {
    if (!campaign?.items) return 0;
    let total = 0;
    for (const cfg of campaign.items) {
      const sel = items[cfg.id];
      if (sel?.selected && sel.qty > 0) total += unitPrice(cfg) * Number(sel.qty);
    }
    return total;
  }, [items, campaign]);

  const toggleItem = (id, checked) => {
    setItems((prev) => ({ ...prev, [id]: { selected: checked, qty: prev[id]?.qty ?? 1 } }));
  };
  const setQty = (id, qty) => {
    const n = Math.max(1, Number(qty) || 1);
    setItems((prev) => ({ ...prev, [id]: { selected: prev[id]?.selected ?? true, qty: n } }));
  };

  const submit = async () => {
    if (!parentId) { toast.error('Pick a parent'); return; }
    const payloadItems = (campaign?.items || [])
      .filter((cfg) => items[cfg.id]?.selected && items[cfg.id]?.qty > 0)
      .map((cfg) => ({ item_type: cfg.id, quantity: items[cfg.id].qty }));
    if (payloadItems.length === 0) { toast.error('Pick at least one item'); return; }
    setSubmitting(true);
    try {
      const isForParent = playerId === '__parent_self__';
      await api.post('/uniform/orders/admin-create', {
        campaign_id: campaign.id,
        parent_id: parentId,
        player_id: isForParent ? null : playerId,
        is_for_parent: isForParent,
        items: payloadItems,
        notes: notes || null,
      });
      toast.success('Order created — Mark Processed to generate the invoice.');
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  if (!campaign) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="admin-add-order-dialog">
        <DialogHeader>
          <DialogTitle>Add Order — {campaign.title}</DialogTitle>
          <DialogDescription>
            Create a uniform order on behalf of a parent (walk-in / phone order).
            Marking it Processed later will auto-generate an unpaid invoice under the Uniform category.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Parent picker */}
          <div className="space-y-1.5">
            <Label>Parent <span className="text-red-500">*</span></Label>
            <Input
              placeholder="Search by name or email…"
              value={parentQuery}
              onChange={(e) => setParentQuery(e.target.value)}
              data-testid="admin-add-order-parent-search"
            />
            <Select value={parentId} onValueChange={setParentId}>
              <SelectTrigger data-testid="admin-add-order-parent-select"><SelectValue placeholder="Select a parent" /></SelectTrigger>
              <SelectContent>
                {filteredParents.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-slate-500">No parents match — refine search</div>
                ) : filteredParents.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`admin-add-order-parent-opt-${p.id}`}>
                    {p.name || p.email || p.id} {p.email && p.name ? `· ${p.email}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Player picker (or "for parent themselves") */}
          {parentId && (
            <div className="space-y-1.5">
              <Label>Order for</Label>
              <Select value={playerId} onValueChange={setPlayerId}>
                <SelectTrigger data-testid="admin-add-order-player-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__parent_self__">For parent themselves</SelectItem>
                  {players.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`admin-add-order-player-opt-${p.id}`}>
                      {p.full_name || p.name || p.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {players.length === 0 && (
                <p className="text-xs text-slate-500">
                  No players found for this parent. The order will be billed to the parent's account.
                </p>
              )}
            </div>
          )}

          {/* Items */}
          <div className="space-y-2">
            <Label>Items <span className="text-red-500">*</span></Label>
            <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
              {(campaign.items || []).map((cfg) => {
                const sel = items[cfg.id] || {};
                return (
                  <div key={cfg.id} className="flex items-center gap-3 px-3 py-2">
                    <Checkbox
                      id={`item-${cfg.id}`}
                      checked={!!sel.selected}
                      onCheckedChange={(v) => toggleItem(cfg.id, !!v)}
                      data-testid={`admin-add-order-item-toggle-${cfg.id}`}
                    />
                    <Label htmlFor={`item-${cfg.id}`} className="flex-1 cursor-pointer">
                      <span className="font-medium">{cfg.label}</span>
                      <span className="text-xs text-slate-500 ml-2">{cs()}{unitPrice(cfg).toFixed(2)}</span>
                    </Label>
                    {sel.selected && (
                      <Input
                        type="number" min="1" className="w-20"
                        value={sel.qty ?? 1}
                        onChange={(e) => setQty(cfg.id, e.target.value)}
                        data-testid={`admin-add-order-item-qty-${cfg.id}`}
                      />
                    )}
                  </div>
                );
              })}
              {(campaign.items || []).length === 0 && (
                <p className="px-3 py-3 text-sm text-slate-500">This campaign has no items configured.</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Textarea
              rows={2} placeholder="Anything to note about this order…"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="admin-add-order-notes"
            />
          </div>

          {/* Total preview */}
          <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm">
            <span className="text-slate-600">Estimated total</span>
            <span className="font-semibold text-slate-800" data-testid="admin-add-order-total">
              {cs()}{computedTotal.toFixed(2)}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !parentId || computedTotal === 0} data-testid="admin-add-order-submit">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Create order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
